import { clsx } from 'clsx'
import { XMarkIcon } from '@heroicons/react/20/solid'

// Tag color mapping (TailwindCSS classes)
export const TAG_COLORS: Record<string, string> = {
  // Agent tags
  explorer: 'bg-blue-500/15 text-blue-600 border-blue-500/20',
  orchestrator: 'bg-purple-500/15 text-purple-600 border-purple-500/20',
  planner: 'bg-green-500/15 text-green-600 border-green-500/20',
  builder: 'bg-orange-500/15 text-orange-600 border-orange-500/20',
  verifier: 'bg-teal-100 text-teal-800 border-teal-200',
  knowledge: 'bg-indigo-500/15 text-primary border-indigo-500/20',
  reporting: 'bg-pink-100 text-pink-800 border-pink-200',
  analysis: 'bg-yellow-500/15 text-yellow-600 border-yellow-500/20',
  observability: 'bg-red-500/15 text-red-600 border-red-500/20',
  automation: 'bg-cyan-500/15 text-cyan-600 border-cyan-500/20',
  // Workflow tags
  n8n: 'bg-orange-500/15 text-orange-600 border-orange-500/20',
  langgraph: 'bg-blue-500/15 text-blue-600 border-blue-500/20',
  crewai: 'bg-purple-500/15 text-purple-600 border-purple-500/20',
  adk: 'bg-green-500/15 text-green-600 border-green-500/20',
  external: 'bg-muted text-foreground border-border',
  internal: 'bg-indigo-500/15 text-primary border-indigo-500/20',
  scheduled: 'bg-yellow-500/15 text-yellow-600 border-yellow-500/20',
  webhook: 'bg-teal-100 text-teal-800 border-teal-200',
  'ai-generated': 'bg-pink-100 text-pink-800 border-pink-200',
  template: 'bg-cyan-500/15 text-cyan-600 border-cyan-500/20',
}

// Default color for unknown tags
const DEFAULT_TAG_COLOR = 'bg-muted text-foreground border-border'

interface TagFilterProps<T extends string> {
  tags: T[]
  selectedTags: T[]
  onTagToggle: (tag: T) => void
  onClear: () => void
  tagColors?: Record<T, string>
  className?: string
}

export function TagFilter<T extends string>({
  tags,
  selectedTags,
  onTagToggle,
  onClear,
  tagColors,
  className,
}: TagFilterProps<T>) {
  const getTagColor = (tag: T, isSelected: boolean) => {
    const colors = tagColors || TAG_COLORS
    const baseColor = colors[tag as string] || DEFAULT_TAG_COLOR

    if (isSelected) {
      // Selected state: filled background
      return baseColor
    }

    // Unselected state: outline style
    return 'bg-card text-muted-foreground border-border hover:border-border'
  }

  return (
    <div className={clsx('flex flex-wrap items-center gap-2', className)}>
      {tags.map((tag) => {
        const isSelected = selectedTags.includes(tag)
        return (
          <button
            key={tag}
            onClick={() => onTagToggle(tag)}
            className={clsx(
              'inline-flex items-center px-3 py-1 text-sm font-medium rounded-full border transition-all duration-150',
              getTagColor(tag, isSelected),
              isSelected && 'ring-2 ring-offset-1 ring-primary-500/30'
            )}
          >
            {tag}
            {isSelected && (
              <XMarkIcon className="w-4 h-4 ml-1.5 -mr-0.5 opacity-60" />
            )}
          </button>
        )
      })}

      {selectedTags.length > 0 && (
        <button
          onClick={onClear}
          className="inline-flex items-center px-3 py-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          Clear all
        </button>
      )}
    </div>
  )
}

// Tag badge component for displaying tags on cards
interface TagBadgeProps {
  tag: string
  size?: 'sm' | 'md'
  className?: string
}

export function TagBadge({ tag, size = 'sm', className }: TagBadgeProps) {
  const colorClass = TAG_COLORS[tag] || DEFAULT_TAG_COLOR

  return (
    <span
      className={clsx(
        'inline-flex items-center font-medium rounded-full border',
        colorClass,
        size === 'sm' && 'px-2 py-0.5 text-xs',
        size === 'md' && 'px-2.5 py-1 text-sm',
        className
      )}
    >
      {tag}
    </span>
  )
}

// Helper to render multiple tag badges
interface TagBadgesProps {
  tags: string[]
  maxVisible?: number
  size?: 'sm' | 'md'
  className?: string
}

export function TagBadges({ tags, maxVisible = 3, size = 'sm', className }: TagBadgesProps) {
  const visibleTags = tags.slice(0, maxVisible)
  const hiddenCount = tags.length - maxVisible

  return (
    <div className={clsx('flex flex-wrap items-center gap-1', className)}>
      {visibleTags.map((tag) => (
        <TagBadge key={tag} tag={tag} size={size} />
      ))}
      {hiddenCount > 0 && (
        <span
          className={clsx(
            'inline-flex items-center font-medium rounded-full bg-muted text-muted-foreground',
            size === 'sm' && 'px-2 py-0.5 text-xs',
            size === 'md' && 'px-2.5 py-1 text-sm'
          )}
        >
          +{hiddenCount}
        </span>
      )}
    </div>
  )
}

export default TagFilter
